import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRES_AT: 'token_expires_at'
};

/**
 * Get token expiry time (Unix seconds) from localStorage or by decoding JWT payload.
 * Supabase default JWT expiry is 1 hour; we use server-provided expires_at when available.
 */
function getTokenExpiresAt() {
  const stored = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return null;
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

function isTokenExpired() {
  const expiresAt = getTokenExpiresAt();
  if (expiresAt == null) return false;
  return Math.floor(Date.now() / 1000) >= expiresAt;
}

function clearSessionStorage() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN));
  const expiryTimerRef = useRef(null);

  // Clear session when JWT has expired (on load and via timer)
  const clearExpiredSession = React.useCallback(() => {
    clearSessionStorage();
    setUser(null);
    setToken(null);
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  // Schedule logout at token expiry so frontend session ends with JWT
  const scheduleExpiryCheck = React.useCallback((expiresAtSeconds) => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (expiresAtSeconds == null) return;
    const nowMs = Date.now();
    const expiresAtMs = expiresAtSeconds * 1000;
    const delayMs = expiresAtMs - nowMs;
    if (delayMs <= 0) {
      clearExpiredSession();
      return;
    }
    expiryTimerRef.current = setTimeout(clearExpiredSession, delayMs);
  }, [clearExpiredSession]);

  // Check if user is logged in on mount; treat expired token as logged out
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (storedToken && isTokenExpired()) {
        clearSessionStorage();
        setLoading(false);
        return;
      }
      if (storedToken) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/auth/user`,
            {},
            { headers: { Authorization: `Bearer ${storedToken}` } }
          );
          if (response.data.success) {
            setUser(response.data.user);
            setToken(storedToken);
            const expiresAt = getTokenExpiresAt();
            scheduleExpiryCheck(expiresAt);
          } else {
            clearSessionStorage();
          }
        } catch (error) {
          clearSessionStorage();
        }
      }
      setLoading(false);
    };

    checkAuth();
    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
    };
  }, [scheduleExpiryCheck]);

  const login = async (email, password) => {
    try {
      // Do not log or expose credentials. Request body is visible in DevTools Network tab; use HTTPS in production.
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        const { user, session } = response.data;
        const accessToken = session.access_token;
        setToken(accessToken);
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        if (session.refresh_token) {
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, session.refresh_token);
        }
        if (session.expires_at != null) {
          localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, String(session.expires_at));
          scheduleExpiryCheck(session.expires_at);
        } else {
          const expiresAt = getTokenExpiresAt();
          scheduleExpiryCheck(expiresAt);
        }
        try {
          const userResponse = await axios.post(
            `${API_BASE_URL}/api/auth/user`,
            {},
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          if (userResponse.data.success && userResponse.data.user) {
            setUser(userResponse.data.user);
          } else {
            setUser(user);
          }
        } catch {
          setUser(user);
        }
        return { success: true };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(
          `${API_BASE_URL}/api/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
      setUser(null);
      setToken(null);
      clearSessionStorage();
    }
  };

  const isAdmin = Array.isArray(user?.role_names) && user.role_names.includes('admin');

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: !!isAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
